export const FILE_AREAS = [
  'dr_reference',
  'wi_attachment',
  'wi_submission',
  'wi_return',
  'bd_dossier',
  'bd_return',
  'data_bank',
  'pharmacopoeia',
  'technical_documents',
  'legacy',
] as const;

export type FileArea = (typeof FILE_AREAS)[number];

/** `legacy` = record imported from the Sheet whose bytes are still only in Google Drive (not yet copied to storage). */
export const FILE_STATUSES = ['pending', 'active', 'deleted', 'legacy'] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];

const MB = 1024 * 1024;
const GB = 1024 * MB;

export const DEFAULT_MAX_UPLOAD_BYTES = 200 * MB;

export const AREA_MAX_UPLOAD_BYTES: Partial<Record<FileArea, number>> = {
  bd_dossier: 2 * GB,
  data_bank: 2 * GB,
  pharmacopoeia: 1 * GB,
  technical_documents: 1 * GB,
};

export function maxUploadBytes(area: FileArea): number {
  return AREA_MAX_UPLOAD_BYTES[area] ?? DEFAULT_MAX_UPLOAD_BYTES;
}

export const ALLOWED_FILE_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'xlsm',
  'csv',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'odt',
  'ods',
  'odp',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'tif',
  'tiff',
  'heic',
  'zip',
  'rar',
  '7z',
  'msg',
  'eml',
] as const;

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : '';
}

export function isAllowedFileName(name: string): boolean {
  return (ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(fileExtension(name));
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}
